import React from 'react';
import { AlertTriangle, Plus, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import PickSelect from '../shared/PickSelect';
import { NumField } from './fields';

// Schedule E Part I columns the LO can edit per property (ticket 199).
const PROPERTY_FIELDS = [
  { k: 'rents_received', label: 'Rents Received (L3)', money: true },
  { k: 'total_expenses', label: 'Total Expenses (L20)', money: true },
  { k: 'mortgage_interest', label: 'Mortgage Interest (L12)', money: true },
  { k: 'taxes_real_estate', label: 'Real Estate Taxes (L16)', money: true },
  { k: 'insurance', label: 'Insurance (L9)', money: true },
  { k: 'depreciation_depletion', label: 'Depreciation (L18)', money: true },
  { k: 'hoa_dues', label: 'HOA Dues', money: true },
  { k: 'months_in_service', label: 'Months Rented (blank = 12)', min: 0, max: 12 },
  { k: 'monthly_piti', label: 'Monthly PITI ($)', money: true },
];

// Ticket 244: the calc is refused for a blank rents, expenses or PITI line on any property.
const REQUIRED_PER_PROPERTY = ['rents_received', 'total_expenses', 'monthly_piti'];

// Ticket 234: Fannie Form 1084 add-backs from the business's own return; the borrower's
// share is these x Ownership %. The last two are subtracted.
const BIZ_ADDBACKS = [
  { k: 'biz_depreciation', label: 'Depreciation ($)' },
  { k: 'biz_depletion', label: 'Depletion ($)' },
  { k: 'biz_amortization', label: 'Amortization or casualty loss ($)' },
  { k: 'biz_notes_payable', label: 'Mortgages/notes payable < 1 yr, subtracted ($)' },
  { k: 'biz_nondeductible_te', label: 'Non-deductible travel & entertainment, subtracted ($)' },
];

// Ticket 234: a K-1 is already the borrower's share; a 1065/1120S return is the whole business's.
const K1_SOURCES = [
  { value: 'k1', label: "K-1 (borrower's share)" },
  { value: 'entity_return', label: 'Business return (1065/1120S)' },
];

function RentalPropertyList({ rows, setRows, miss }) {
  const setCell = (i, k) => (e) => setRows(rows.map((r, j) => (
    j === i ? { ...r, [k]: e.target.value === '' ? undefined : Number(e.target.value) } : r
  )));
  return (
    <div className="space-y-3" data-testid="rental-property-list">
      {rows.map((r, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50 space-y-3" data-testid={`rental-property-${i}`}>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Property {i + 1} address</Label>
              <Input
                value={r.property_address || ''}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, property_address: e.target.value } : x)))}
                maxLength={120}
                className="mt-1 h-9 text-sm"
                data-testid={`rental-property-address-${i}`}
              />
            </div>
            <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-1.5 hover:bg-red-50 rounded-lg mb-0.5" aria-label={`Remove property ${i + 1}`} data-testid={`rental-property-remove-${i}`}>
              <X size={14} className="text-red-500" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {PROPERTY_FIELDS.map((f) => (
              <NumField
                key={f.k}
                label={f.label}
                value={r[f.k]}
                onChange={setCell(i, f.k)}
                missing={REQUIRED_PER_PROPERTY.includes(f.k) && miss(`${f.k}_${i}`, r[f.k])}
                money={f.money}
                min={f.min}
                max={f.max}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Per-income-type input fieldsets for the manual calculator.
// `missing` = field names the last calc was refused for (ticket 139); outlined while still blank.
export function IncomeTypeFields({ type, form, setForm, setNum, missing = new Set() }) {
  const miss = (f, value = form[f]) => missing.has(f) && (value == null || value === '');
  const rentalRows = form.rental_properties || [];
  const setRentalRows = (rows) => setForm({ ...form, rental_properties: rows.length ? rows : undefined });
  return (
    <>
      {type === 'w2_wage' && <>
        <div className="text-[10px] uppercase tracking-widest font-bold text-brand-accent">Base Pay (leave blank the fields that don't apply)</div>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Prior-Year Annual Base ($)" value={form.y1_gross} onChange={setNum('y1_gross')} missing={miss('y1_gross')} money />
          <NumField label="Current Annual Base ($)" value={form.y2_gross} onChange={setNum('y2_gross')} missing={miss('y2_gross')} money />
          <NumField label="YTD Gross ($)" value={form.ytd_gross} onChange={setNum('ytd_gross')} money />
          <NumField label="YTD Months" value={form.ytd_months} onChange={setNum('ytd_months')} step="0.1" />
        </div>
        <div className="text-[10px] uppercase tracking-widest font-bold pt-2 text-brand-accent">OR Hourly (auto-detected when present)</div>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Hourly Rate ($)" value={form.hourly_rate} onChange={setNum('hourly_rate')} missing={miss('hourly_rate')} step="0.01" />
          <NumField label="Hours per Week" value={form.hours_per_week} onChange={setNum('hours_per_week')} step="0.5" />
        </div>
        <div className="text-[10px] uppercase tracking-widest font-bold pt-2 flex items-center gap-1" style={{ color: '#f59e0b' }}>
          <AlertTriangle size={11} /> Variable Pay (24-mo avg) — Overtime + Bonus + Commission
        </div>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Year 1 Variable ($)" value={form.overtime_y1} onChange={setNum('overtime_y1')} missing={miss('overtime_y1')} money />
          <NumField label="Year 2 Variable ($)" value={form.overtime_y2} onChange={setNum('overtime_y2')} missing={miss('overtime_y2')} money />
        </div>
        <div className="text-[10px] text-slate-500 leading-relaxed pt-1">
          Auto-detected from your uploads. If any variable pay is present, we automatically request a year-end paystub + W-VOE to satisfy the 24-month history rule.
        </div>
      </>}
      {type === 'self_employed_sch_c' && <>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Y1 Net Profit ($)" value={form.net_profit_y1} onChange={setNum('net_profit_y1')} missing={miss('net_profit_y1')} money />
          <NumField label="Y2 Net Profit ($)" value={form.net_profit_y2} onChange={setNum('net_profit_y2')} missing={miss('net_profit_y2')} money />
        </div>
        <div className="text-xs uppercase tracking-widest mt-3 text-brand-accent">Allowable Add-Backs</div>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Depreciation ($)" value={form.add_backs_depreciation} onChange={setNum('add_backs_depreciation')} money />
          <NumField label="Amortization ($)" value={form.add_backs_amortization} onChange={setNum('add_backs_amortization')} money />
          <NumField label="Business Use of Home ($)" value={form.add_backs_business_use_home} onChange={setNum('add_backs_business_use_home')} money />
          <NumField label="Meals exclusion, subtracted ($)" value={form.add_backs_meals_50pct} onChange={setNum('add_backs_meals_50pct')} money />
        </div>
      </>}
      {type === 'k1' && <>
        <PickSelect
          label="Source document"
          value={form.k1_source || 'k1'}
          onChange={(v) => setForm({ ...form, k1_source: v === 'entity_return' ? 'entity_return' : undefined })}
          options={K1_SOURCES}
          testId="k1-source"
          labelClassName="text-xs"
        />
        <NumField label="K-1 Ordinary Income (annual $)" value={form.k1_ordinary} onChange={setNum('k1_ordinary')} missing={miss('k1_ordinary')} money />
        <NumField label="K-1 Guaranteed Payments (annual $)" value={form.k1_guaranteed} onChange={setNum('k1_guaranteed')} missing={miss('k1_guaranteed')} money />
        {form.k1_source !== 'entity_return' && <>
          <NumField label="Prior-Year K-1 Ordinary (annual $)" value={form.k1_ordinary_y1} onChange={setNum('k1_ordinary_y1')} missing={miss('k1_ordinary_y1')} money />
          <NumField label="Prior-Year K-1 Guaranteed (annual $)" value={form.k1_guaranteed_y1} onChange={setNum('k1_guaranteed_y1')} missing={miss('k1_guaranteed_y1')} money />
        </>}
        <NumField label={form.k1_source === 'entity_return' ? 'Ownership % (business return — required)' : 'Ownership %'} value={form.ownership_pct} onChange={setNum('ownership_pct')} missing={miss('ownership_pct')} min={0} max={100} step="0.01" />
        <div className="text-xs uppercase tracking-widest mt-3 text-brand-accent">Business return add-backs (whole business)</div>
        <div className="grid grid-cols-2 gap-3">
          {BIZ_ADDBACKS.map(({ k, label }) => (
            <NumField key={k} label={label} value={form[k]} onChange={setNum(k)} money />
          ))}
        </div>
      </>}
      {type === 'rental' && <>
        {rentalRows.length > 0 ? (
          <>
            <div className="text-[10px] uppercase tracking-widest font-bold text-brand-accent">Schedule E — one row per property</div>
            <RentalPropertyList rows={rentalRows} setRows={setRentalRows} miss={miss} />
          </>
        ) : (
          <>
            <NumField label="Gross Monthly Rent ($)" value={form.gross_rents_monthly} onChange={setNum('gross_rents_monthly')} missing={miss('gross_rents_monthly')} money />
            <NumField label="Monthly PITI ($)" value={form.piti_monthly} onChange={setNum('piti_monthly')} missing={miss('piti_monthly')} money />
          </>
        )}
        <button
          type="button"
          onClick={() => setRentalRows([...rentalRows, {}])}
          className="inline-flex items-center gap-1 text-xs font-semibold text-brand-navy hover:underline"
          data-testid="rental-property-add"
        >
          <Plus size={12} /> {rentalRows.length > 0 ? 'Add a property' : 'Enter Schedule E properties instead'}
        </button>
      </>}
      {type === 'retirement' && <>
        <NumField label="Annual Retirement / SS / Pension ($)" value={form.retirement_annual} onChange={setNum('retirement_annual')} missing={miss('retirement_annual')} money />
        <NumField label="Remaining Term (years)" value={form.remaining_term_years} onChange={setNum('remaining_term_years')} />
      </>}
      {type === 'bank_statement' && <>
        <NumField label="Avg Monthly Deposits ($)" value={form.bank_deposits_monthly_avg} onChange={setNum('bank_deposits_monthly_avg')} missing={miss('bank_deposits_monthly_avg')} money />
        <NumField label="Expense Ratio (%)" value={form.bank_expense_ratio} onChange={setNum('bank_expense_ratio')} step="0.01" />
      </>}
    </>
  );
}
